import { LayoutPanelLeft, Gavel, Truck, FileText, Activity, MessageSquare, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type LoadDetailTabKey =
  | "overview"
  | "bids"
  | "dispatch"
  | "documents"
  | "timeline"
  | "messages";

interface TabDef {
  key: LoadDetailTabKey;
  label: string;
  icon: LucideIcon;
}

const TABS: TabDef[] = [
  { key: "overview", label: "Overview", icon: LayoutPanelLeft },
  { key: "bids", label: "Bids", icon: Gavel },
  { key: "dispatch", label: "Dispatch", icon: Truck },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "timeline", label: "Timeline", icon: Activity },
  { key: "messages", label: "Messages", icon: MessageSquare },
];

interface Props {
  value: LoadDetailTabKey;
  onValueChange: (key: LoadDetailTabKey) => void;
  /** Real counts. 0 / undefined renders no badge. */
  counts?: Partial<Record<LoadDetailTabKey, number>>;
  /** Tabs whose badge should read as an alert (e.g. unread messages). */
  alertTabs?: LoadDetailTabKey[];
}

export function LoadDetailTabsNav({ value, onValueChange, counts, alertTabs = [] }: Props) {
  return (
    <nav
      aria-label="Load sections"
      className="rounded-xl border border-border bg-card shadow-sm p-1.5 md:p-2"
    >
      <div
        role="tablist"
        className="flex items-center gap-1.5 overflow-x-auto -mx-0.5 px-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {TABS.map((t) => {
          const active = value === t.key;
          const count = counts?.[t.key] ?? 0;
          const isAlert = alertTabs.includes(t.key);
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={active}
              onClick={() => onValueChange(t.key)}
              className={cn(
                "group relative flex flex-shrink-0 items-center gap-2 rounded-lg px-3.5 md:px-4 h-10 md:h-11",
                "text-sm font-medium whitespace-nowrap",
                "motion-safe:transition-all motion-safe:duration-200 motion-safe:ease-out",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                active
                  ? "bg-brand-teal text-white font-semibold shadow-md"
                  : "border border-border bg-background text-foreground hover:border-brand-teal/40 hover:text-brand-teal hover:bg-brand-teal/5 motion-safe:hover:-translate-y-px"
              )}
            >
              <Icon className={cn("h-4 w-4 flex-shrink-0", active ? "text-white" : "text-muted-foreground group-hover:text-brand-teal")} />
              <span>{t.label}</span>
              {count > 0 && (
                <span
                  className={cn(
                    "ml-0.5 inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full px-1.5",
                    "text-[10px] font-bold tabular-nums",
                    active
                      ? "bg-white/25 text-white"
                      : isAlert
                        ? "bg-destructive text-destructive-foreground"
                        : "bg-muted text-muted-foreground"
                  )}
                  aria-label={`${count} ${isAlert ? "unread" : "items"}`}
                >
                  {count > 99 ? "99+" : count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
